import React, { useMemo, useTransition } from 'react'

import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { useCollectionFilterQueryParamsContext } from '../../../../../../../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { VisualFilterTile } from '../../../../../../../../../types/graphql/collection-view'
import { LayoutVariant } from '../../../../../../../TileLayout'
import { useCollectionFiltersContext } from '../../../containers/CollectionFiltersContext'
import { coreStylesVisibleTilesDesktop, coreStylesVisibleTilesMobile } from '../VisualFilters'

import {
  Body,
  BulletPointIcon,
  BulletPointRow,
  BulletPoints,
  FilterCtaText,
  FilterDescription,
  Image,
  ImageDisabled,
  ImageWrapper,
  SelectIcon,
} from './styles'

interface Props extends VisualFilterTile {
  imagePriority: 'preload' | 'lazy' | 'default'
  desktopLayout: LayoutVariant
}

export const CoreStylesVisualFilterSectionTile: React.FC<Props> = ({
  ctaText,
  image,
  id,
  value,
  filter: filterKeyFromField,
  filterSpec,
  description,
  iconBadges,
  imagePriority = 'default',
  desktopLayout,
}) => {
  const { updateFilterSelection, selectedFilters } = useCollectionFilterQueryParamsContext()
  const { filters } = useCollectionFiltersContext()

  const filterKey = filterSpec?.name ?? filterKeyFromField ?? ''

  const [isUpdateFiltersTransitionPending, startUpdateFiltersTransition] = useTransition()
  const isChecked = useMemo(() => {
    const includedInSelectedFilters = Boolean(selectedFilters[filterKey]?.includes(value))
    return isUpdateFiltersTransitionPending ? !includedInSelectedFilters : includedInSelectedFilters
  }, [filterKey, isUpdateFiltersTransitionPending, selectedFilters, value])

  const filterOptions = useMemo(
    () => filters?.find(f => f.name === filterKey),
    [filters, filterKey]
  )

  const isDisabled = useMemo(
    () => filterOptions?.values.some(o => o.value === value && o.state === 'DISABLED'),
    [filterOptions?.values, value]
  )

  const toggleChecked = () => {
    if (isUpdateFiltersTransitionPending) return
    startUpdateFiltersTransition(() => {
      updateFilterSelection(filterKey, value, !isChecked)
    })
  }

  return (
    <Body $isChecked={isChecked} $layoutDesktop={desktopLayout}>
      {!isDisabled ? (
        <label key={id} htmlFor={`checkbox-${id}`} style={{ position: 'relative' }}>
          <input
            type='checkbox'
            id={`checkbox-${id}`}
            style={{ opacity: '0', position: 'absolute', inset: '0' }}
            checked={isChecked}
            onChange={toggleChecked}
          />
          <ImageWrapper $isChecked={isChecked}>
            <Image
              alt={''}
              loading={imagePriority === 'lazy' ? 'lazy' : 'default'}
              src={image}
              aspectRatios={{
                sm: 4 / 5,
                md: 4 / 5,
              }}
              widths={{
                unit: 'vw',
                sm: 100 / coreStylesVisibleTilesMobile,
                md: 100 / coreStylesVisibleTilesDesktop,
              }}
              fetchPriority={imagePriority === 'preload' ? 'high' : 'auto'}
            />
            <SelectIcon $active={isChecked} />
          </ImageWrapper>
          <FilterCtaText
            isUppercase={isUppercase(ctaText)}
            // TODO: Fix this auto-generated suppression.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            isDisabled={isDisabled || false}
            isChecked={isChecked}
            onClick={() => {
              // TODO: Fix this auto-generated suppression.
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              if (!isDisabled) toggleChecked()
            }}
          >
            {ctaText}
          </FilterCtaText>
          {!iconBadges.length && description ? (
            <FilterDescription
              isUppercase={isUppercase(description)}
              // TODO: Fix this auto-generated suppression.
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              isDisabled={isDisabled || false}
              isChecked={isChecked}
            >
              {description}
            </FilterDescription>
          ) : null}
          {iconBadges.length ? (
            <BulletPoints>
              {iconBadges.map(iconBadge =>
                iconBadge.icon && iconBadge.iconText ? (
                  <BulletPointRow>
                    <BulletPointIcon
                      src={iconBadge.icon}
                      alt={iconBadge.caption ?? ''}
                      aspectRatios={{ sm: 1, md: 1, lg: 1 }}
                      loading='lazy'
                      widths={{ unit: 'px', sm: 100, md: 100, lg: 100 }}
                    />
                    {iconBadge.iconText}
                  </BulletPointRow>
                ) : null
              )}
            </BulletPoints>
          ) : null}
        </label>
      ) : (
        <>
          <ImageWrapper $isChecked={false}>
            <ImageDisabled
              alt={ctaText}
              src={image}
              loading={imagePriority === 'lazy' ? 'lazy' : 'default'}
              aspectRatios={{
                sm: 145 / 172,
                md: 253 / 328,
              }}
              widths={{
                unit: 'vw',
                sm: 100 / coreStylesVisibleTilesMobile,
                md: 100 / coreStylesVisibleTilesDesktop,
              }}
              fetchPriority={imagePriority === 'preload' ? 'high' : 'auto'}
            />
          </ImageWrapper>
          <FilterCtaText isUppercase={isUppercase(ctaText)} isDisabled={true} isChecked={false}>
            {ctaText}
          </FilterCtaText>
          {description ? (
            <FilterDescription
              isUppercase={isUppercase(description)}
              isDisabled={true}
              isChecked={false}
            >
              {description}
            </FilterDescription>
          ) : null}
        </>
      )}
    </Body>
  )
}
